import { Component, Output, Input, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NestedOptionHost } from 'devextreme-angular';
import { DxTreeViewComponent } from 'devextreme-angular/ui/tree-view';

import * as events from 'devextreme/events';
import { SideNavItemModel } from '../../../material-design/models';
import { HhsgSharedUserProfile } from '../../../models/user-profile.model';
import { ScreenService } from '../../../services/screen.service';
import { RouteUtil } from '../../../utilities/route.util';

@Component({
  selector: 'huron-side-navigation-menu',
  templateUrl: './side-navigation-menu.component.html',
  styleUrls: ['./side-navigation-menu.component.scss'],
  providers: [ NestedOptionHost ]
})
export class HuronSideNavigationMenuComponent implements AfterViewInit, OnDestroy {
  @ViewChild(DxTreeViewComponent, { static: true })
  menu: DxTreeViewComponent;

  @Output()
  selectedItemChanged = new EventEmitter<string>();

  @Output()
  openMenu = new EventEmitter<any>();

  @Output()
  menuToggle = new EventEmitter<boolean>();

  private _user: HhsgSharedUserProfile;
  @Input() set user(value: HhsgSharedUserProfile) {
    this._user = value;
    this.resetSideNavBasedOnUserProfileAndScreenSize();
  } get user() {
    return this._user;
  }

  private _selectedItem: String;
  @Input()
  set selectedItem(value: String) {
    this._selectedItem = value;
    if (!this.menu.instance || !this._selectedItem || this.selectedItem == "") {
      return;
    }

    this.menu.instance.selectItem(value);
  }

  private _filteredNavItems;
  private _unfilteredNavItems;
  @Input() set sideNavItems(value: SideNavItemModel[]) {
    let unfilteredItems = value.map((item) => {
      this.formatRouteUrl(item);
       return {
        ...item,
        expanded: false
      };
    });
    this._unfilteredNavItems = unfilteredItems;


    if (RouteUtil.routeExists(this._router, 'logout')) {
      var logoutMenuItem = new SideNavItemModel();
      logoutMenuItem.icon = 'power_rounded';
      logoutMenuItem.name = 'Logout';
      logoutMenuItem.routeUrl = 'logout';
      logoutMenuItem.onlyShowOnCompactScreen = true;
      this._unfilteredNavItems.push(logoutMenuItem);
    }

    this.resetSideNavBasedOnUserProfileAndScreenSize();
  }

  get sideNavItems() {
    return this._filteredNavItems;
  }

  private _compactMode = false;
  @Input()
  get compactMode() {
    return this._compactMode;
  }
  set compactMode(val) {
    this._compactMode = val;

    if (!this.menu.instance) {
      return;
    }

    if (val) {
      this.menu.instance.collapseAll();
    } else {
      this.menu.instance.expandItem(this._selectedItem);
    }
  }

  constructor(private elementRef: ElementRef,
    private _router: Router,
    private screen: ScreenService) {

    this.screen.changed.subscribe(() => this.resetSideNavBasedOnUserProfileAndScreenSize());
  }

  // Collapse all items that are not the item clicked on, but not parent folders:
  onItemExpanded(event) {
    let itemPaths = this.findINavItemParents(event.itemData.name, this.sideNavItems);
    this.sideNavItems.forEach(item => {
      if (!itemPaths.includes(item.name)) {
        this.menu.instance.collapseItem(item);
      }
    });
  }

  findINavItemParents(name: string, items: SideNavItemModel[]): string[] {
    let currentPath: string[] = [];
    for (const item of items) {
      if (item.items?.length > 0) {
        const path = this.findItemParent(name, item.items);
        if (path && path.length > 0) {
          currentPath = [...path];
          currentPath.push(item.name);
        }
      }
    }
    if (currentPath.length == 0) {
      currentPath.push(name);
    }
    return currentPath;
  }

  findItemParent(name: string, items: SideNavItemModel[], currentPath: string[] = []): string[] | null {
    for (const item of items) {
      if (item.name == name) {
        currentPath.push(name);
        return currentPath;
      }
      else {
        if (item.items?.length > 0) {
          const path = this.findItemParent(name, item.items, [...currentPath]);
          if (path) {
            return path;
          }
        }
      }
    };

    return currentPath;
  }

  onItemClick(event) {
    this.selectedItem = event.itemData;
    this.selectedItemChanged.emit(event);
  }

  ngAfterViewInit() {
    events.on(this.elementRef.nativeElement, 'dxclick', (e) => {
      this.openMenu.next(e);
    });
  }

  ngOnDestroy() {
    events.off(this.elementRef.nativeElement, 'dxclick');
  }

  toggleMenu = () => {
    this.menuToggle.emit();
  }

  private formatRouteUrl(item: SideNavItemModel) {
    if (item.routeUrl && !(/^\//.test(item.routeUrl))) {
      item.routeUrl = `/${item.routeUrl}`;
    }
  }

	public userCanAccessPage(menuItem: SideNavItemModel) : boolean {
		if (menuItem?.roles) {
			let hasRoleResult = false;
			if (this.user) {
				menuItem.roles.forEach(role => {
					let userHasRole = this.hasRole(role, this.user.roles);
					if (userHasRole) {
						hasRoleResult = true;
					}
				});
			}
			return hasRoleResult;
		} else {
			return true;
		}
	}

  private hasRole(roleName: string, roles: string[]) : boolean {
    return roles && roles.findIndex(role => role == roleName) >= 0;
  }

  private resetSideNavBasedOnUserProfileAndScreenSize() {
    let filteredItems = this.filterItemsBasedOnRoleAndScreenSize(this._unfilteredNavItems);
    filteredItems.forEach(parentItem => {
      if (parentItem.items) {
        parentItem.items = this.filterItemsBasedOnRoleAndScreenSize(parentItem.items);
        parentItem.items.forEach(childItem => {
          if (childItem.items) {
            childItem.items = this.filterItemsBasedOnRoleAndScreenSize(childItem.items);
          }
        });
      }
    });

    this._filteredNavItems = filteredItems;
  }

  private filterItemsBasedOnRoleAndScreenSize(navItems: SideNavItemModel[]) : SideNavItemModel[] {
    let filteredItems = navItems.filter(item => this.userCanAccessPage(item));
    if (this.screen.isCompact()) {
      filteredItems = filteredItems.filter(item => (item.onlyShowOnCompactScreen === true || item.onlyShowOnCompactScreen == null) && !item.hideOnCompactScreen)
    }
    else {
      filteredItems = filteredItems.filter(item => (item.onlyShowOnCompactScreen == false || item.onlyShowOnCompactScreen == null));
    }

    return filteredItems;
  }

}
